/**
 * Project push + pull — move a whole project (its document + the media it uses)
 * between this device and the cloud.
 *
 * The cloud stores a **self-contained bundle** per project: the project document
 * AND the full metadata for every media it references. That metadata is what lets
 * a fresh device render the timeline before any bytes are downloaded — the bytes
 * themselves come down on demand via getMediaFile's cloud fallback.
 *
 * Dependencies are injected (real defaults) so the unit tests run with fakes.
 */

import type { BackendClient } from '@/infrastructure/backend'
import type { MediaMetadata } from '@/types/storage'
import type { Project } from '@/types/project'
import {
  getProject,
  createProject,
  updateProject,
  getMedia,
  createMedia,
  associateMediaWithProject,
} from '@/infrastructure/storage'

import { mediaLibraryService } from '../deps/media-library'
import { pushProjectMedia, type MediaPushOptions, type MediaPushResult } from './media-push-service'

/** MediaMetadata without the non-serializable file handle (safe to send/store). */
export type SerializedMedia = Omit<MediaMetadata, 'fileHandle'>

/** The self-contained snapshot stored in the cloud `project` field. */
export interface CloudProjectBundle {
  /** The project document (no `rootFolderHandle`). */
  doc: Record<string, unknown>
  /** Full metadata for every media the project uses. */
  media: SerializedMedia[]
}

/** Drop the origin-scoped file handle so metadata is JSON-safe. */
function stripHandle(m: MediaMetadata): SerializedMedia {
  const copy: Record<string, unknown> = { ...m }
  delete copy.fileHandle
  return copy as unknown as SerializedMedia
}

/* ────────────────────────────── Push ─────────────────────────────────── */

export interface ProjectPushOptions {
  /** Forwarded to the media push so callers can show per-file progress. */
  media?: MediaPushOptions
}

export interface ProjectPushResult {
  media: MediaPushResult
}

export interface ProjectToPush {
  name: string
  updatedAt: number
  bundle: CloudProjectBundle
}

export interface ProjectSyncDeps {
  getProjectToPush: (projectId: string) => Promise<ProjectToPush | null>
  pushMedia: (
    projectId: string,
    client: BackendClient,
    options?: MediaPushOptions,
  ) => Promise<MediaPushResult>
}

export const defaultProjectSyncDeps: ProjectSyncDeps = {
  getProjectToPush: async (projectId) => {
    const project = await getProject(projectId)
    if (!project) return null
    // Strip the non-serializable folder handle from the document.
    const doc: Record<string, unknown> = { ...project }
    delete doc.rootFolderHandle
    // Full metadata for the project's media (handles stripped).
    const records = await mediaLibraryService.getMediaForProject(projectId)
    const media = records.map(stripHandle)
    return { name: project.name, updatedAt: project.updatedAt, bundle: { doc, media } }
  },
  pushMedia: pushProjectMedia,
}

/**
 * Push a project and its media to the cloud.
 *
 * Order: media bytes first (so cloud records + r2Keys exist), then the project
 * bundle that references them.
 */
export async function pushProject(
  projectId: string,
  client: BackendClient,
  options: ProjectPushOptions = {},
  deps: ProjectSyncDeps = defaultProjectSyncDeps,
): Promise<ProjectPushResult> {
  const info = await deps.getProjectToPush(projectId)
  if (!info) throw new Error(`Project not found: ${projectId}`)

  const media = await deps.pushMedia(projectId, client, options.media)
  await client.putProject(projectId, {
    name: info.name,
    updatedAt: info.updatedAt,
    project: info.bundle,
  })

  return { media }
}

/* ────────────────────────────── Pull ─────────────────────────────────── */

export interface ProjectPullResult {
  /** How many media records the pulled project references. */
  mediaCount: number
}

export interface ProjectPullDeps {
  /** Create or update the local project from the pulled document. */
  upsertProject: (id: string, doc: Record<string, unknown>) => Promise<void>
  /** Create a local media record if one doesn't already exist. */
  ensureMediaRecord: (meta: SerializedMedia) => Promise<void>
  /** Link a media to the project locally. */
  associateMedia: (projectId: string, mediaId: string) => Promise<void>
}

export const defaultProjectPullDeps: ProjectPullDeps = {
  upsertProject: async (id, doc) => {
    const existing = await getProject(id)
    const next = { ...doc, id } as unknown as Project
    if (existing) {
      await updateProject(id, next)
    } else {
      await createProject(next)
    }
  },
  ensureMediaRecord: async (meta) => {
    const existing = await getMedia(meta.id)
    if (existing) return
    // Clear origin-scoped pointers: this device has neither the handle nor an
    // OPFS copy, so getMediaFile will fall through to the R2 cloud download.
    await createMedia({ ...meta, fileHandle: undefined, opfsPath: undefined } as MediaMetadata)
  },
  associateMedia: associateMediaWithProject,
}

/** Read the cloud `project` field, tolerating the legacy plain-doc format. */
function unwrapBundle(project: unknown): CloudProjectBundle {
  if (project && typeof project === 'object' && 'doc' in project) {
    const p = project as { doc: Record<string, unknown>; media?: SerializedMedia[] }
    return { doc: p.doc, media: p.media ?? [] }
  }
  // Legacy push: the whole object is the document, no media list travelled with it.
  return { doc: (project ?? {}) as Record<string, unknown>, media: [] }
}

/**
 * Pull a project from the cloud into this device's workspace: write the project
 * document and recreate its media records so it opens and plays (bytes download
 * on demand).
 */
export async function pullProject(
  projectId: string,
  client: BackendClient,
  deps: ProjectPullDeps = defaultProjectPullDeps,
): Promise<ProjectPullResult> {
  const res = await client.getProject(projectId)
  const bundle = unwrapBundle(res.project)

  await deps.upsertProject(projectId, bundle.doc)
  for (const m of bundle.media) {
    await deps.ensureMediaRecord(m)
    await deps.associateMedia(projectId, m.id)
  }

  return { mediaCount: bundle.media.length }
}
